import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api } from '@/utils/api';
import type { Tag, EntityTag, EntityType } from '@/types';

interface TagsState {
  tags: Tag[];
  entityTags: Record<string, EntityTag[]>; // key = `${entityType}-${entityId}`
  availableTags: Record<string, (Tag & { isApplied: boolean; isBlocked: boolean })[]>;
  isLoading: boolean;
  error: string | null;
}

const initialState: TagsState = {
  tags: [],
  entityTags: {},
  availableTags: {},
  isLoading: false,
  error: null,
};

// Fetch all user tags
export const fetchTags = createAsyncThunk(
  'tags/fetchTags',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/tags');
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch tags'
      );
    }
  }
);

// Create a new tag
export const createTag = createAsyncThunk(
  'tags/createTag',
  async (data: { name: string; color?: string; description?: string }, { rejectWithValue }) => {
    try {
      const response = await api.post('/tags', data);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to create tag'
      );
    }
  }
);

// Update a tag
export const updateTag = createAsyncThunk(
  'tags/updateTag',
  async ({ id, data }: { id: string; data: { name?: string; color?: string; description?: string } }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/tags/${id}`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to update tag'
      );
    }
  }
);

// Delete a tag
export const deleteTag = createAsyncThunk(
  'tags/deleteTag',
  async (id: string, { rejectWithValue }) => {
    try {
      await api.delete(`/tags/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to delete tag'
      );
    }
  }
);

// Assign tag to entity
export const assignTag = createAsyncThunk(
  'tags/assignTag',
  async ({ tagId, entityType, entityId }: { tagId: string; entityType: EntityType; entityId: string }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/tags/${tagId}/assign`, { entityType, entityId });
      return { tagId, entityType, entityId, ...response.data };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to assign tag'
      );
    }
  }
);

// Unassign tag from entity
export const unassignTag = createAsyncThunk(
  'tags/unassignTag',
  async ({ tagId, entityType, entityId }: { tagId: string; entityType: EntityType; entityId: string }, { rejectWithValue }) => {
    try {
      await api.delete(`/tags/${tagId}/unassign`, { data: { entityType, entityId } });
      return { tagId, entityType, entityId };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to unassign tag'
      );
    }
  }
);

// Fetch tags for an entity
export const fetchEntityTags = createAsyncThunk(
  'tags/fetchEntityTags',
  async ({ entityType, entityId }: { entityType: EntityType; entityId: string }, { rejectWithValue }) => {
    try {
      const response = await api.get(`/tags/entity/${entityType}/${entityId}`);
      return { entityType, entityId, tags: response.data };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch entity tags'
      );
    }
  }
);

// Fetch available tags for an entity
export const fetchAvailableTags = createAsyncThunk(
  'tags/fetchAvailableTags',
  async ({ entityType, entityId }: { entityType: EntityType; entityId: string }, { rejectWithValue }) => {
    try {
      const response = await api.get(`/tags/entity/${entityType}/${entityId}/available`);
      return { entityType, entityId, tags: response.data };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch available tags'
      );
    }
  }
);

const tagsSlice = createSlice({
  name: 'tags',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch tags
      .addCase(fetchTags.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchTags.fulfilled, (state, action) => {
        state.isLoading = false;
        state.tags = action.payload;
      })
      .addCase(fetchTags.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Create tag
      .addCase(createTag.fulfilled, (state, action) => {
        state.tags.push(action.payload);
      })
      // Update tag
      .addCase(updateTag.fulfilled, (state, action) => {
        const index = state.tags.findIndex((t) => t.id === action.payload.id);
        if (index !== -1) {
          state.tags[index] = action.payload;
        }
      })
      // Delete tag
      .addCase(deleteTag.fulfilled, (state, action) => {
        state.tags = state.tags.filter((t) => t.id !== action.payload);
        // Also remove from entity tags
        Object.keys(state.entityTags).forEach((key) => {
          state.entityTags[key] = state.entityTags[key].filter((t) => t.id !== action.payload);
        });
      })
      // Fetch entity tags
      .addCase(fetchEntityTags.fulfilled, (state, action) => {
        const key = `${action.payload.entityType}-${action.payload.entityId}`;
        state.entityTags[key] = action.payload.tags;
      })
      // Fetch available tags
      .addCase(fetchAvailableTags.fulfilled, (state, action) => {
        const key = `${action.payload.entityType}-${action.payload.entityId}`;
        state.availableTags[key] = action.payload.tags;
      })
      // Assign tag - refetch entity tags
      .addCase(assignTag.fulfilled, (state, action) => {
        // Mark for refetch by clearing the cache
        const key = `${action.payload.entityType}-${action.payload.entityId}`;
        delete state.entityTags[key];
        delete state.availableTags[key];
      })
      // Unassign tag - refetch entity tags
      .addCase(unassignTag.fulfilled, (state, action) => {
        const key = `${action.payload.entityType}-${action.payload.entityId}`;
        delete state.entityTags[key];
        delete state.availableTags[key];
      });
  },
});

export const { clearError } = tagsSlice.actions;
export default tagsSlice.reducer;
